import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Card, Icon, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import LinearProgress from '@mui/material/LinearProgress'
import { compact, isNil } from 'lodash'
import { getDate } from 'common/utils/DateUtil'
import { formatMilliers, truncateString } from 'common/utils/StringUtil'
import { PASTEL_COLOR } from 'common/utils/ColorUtil'
import { dueDateColor, progressBarSx, readableInk, statusColor, statusLabel, ticketsLabel } from './LotsUtils'
import { DueDate, MissingBcIcon, StatusChip } from './LotsCommon'

// -------------------------------------------------------------------------------------------------
// Frise chronologique des notes d'avancement
// -------------------------------------------------------------------------------------------------
const Frise = ({ notes = [], echeance, statut, dueColor, onEditNote }) => {
    if (!notes.length) {
        return <Typography color='textSecondary' sx={{ fontSize: 13 }}>Aucune note d&apos;avancement</Typography>
    }
    const sorted = [...notes].sort((a, b) => a.date - b.date)
    const start = sorted[0].date
    const lastDate = sorted[sorted.length - 1].date
    const end = Math.max(lastDate, echeance ?? 0)
    const span = end - start
    const MINP = 5
    const MAXP = 95
    const posOf = (d) => (span > 0 ? MINP + ((d - start) / span) * (MAXP - MINP) : 50)
    const showEcheance = !isNil(echeance)
    const echeanceOverdue = dueColor === PASTEL_COLOR.red

    // Jalons (notes + échéance) ordonnés par date, espacés d'au moins MIN_GAP % vers la droite
    // (passe avant). Si la grappe déborde à droite, on recale le dernier sur MAXP et on remonte
    // vers la gauche (passe arrière) ; en dernier recours (pas assez de place) on répartit
    // uniformément dans [MINP, MAXP]. La frise reste ainsi dans la carte, sans chevauchement.
    const MIN_GAP = 9
    const markers = sorted.map((n, i) => ({ date: n.date, note: i }))
    if (showEcheance) markers.push({ date: echeance, note: -1 })
    markers.sort((a, b) => (a.date - b.date) || ((a.note === -1 ? 1 : 0) - (b.note === -1 ? 1 : 0)))
    markers.forEach((m, i) => {
        m.pos = i === 0 ? posOf(m.date) : Math.max(posOf(m.date), markers[i - 1].pos + MIN_GAP)
    })
    if (markers[markers.length - 1].pos > MAXP) {
        markers[markers.length - 1].pos = MAXP
        for (let i = markers.length - 2; i >= 0; i--) {
            markers[i].pos = Math.min(markers[i].pos, markers[i + 1].pos - MIN_GAP)
        }
    }
    if (markers[0].pos < MINP) {
        const n = markers.length
        markers.forEach((m, i) => {
            m.pos = n === 1 ? 50 : MINP + (i / (n - 1)) * (MAXP - MINP)
        })
    }
    const positions = []
    markers.forEach(m => {
        if (m.note >= 0) positions[m.note] = m.pos
    })
    const echeancePos = showEcheance ? markers.find(m => m.note === -1).pos : null
    const echColor = dueColor ?? '#98a3b8'

    return (
        <Box sx={{ position: 'relative', height: 58, overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', left: `${MINP}%`, right: `${100 - MAXP}%`, top: 10, height: 2, backgroundColor: '#dfe4ec' }} />
            {sorted.slice(0, -1).map((n, i) => (
                <Box key={`seg-${n.id ?? i}`} sx={{ position: 'absolute', left: `${positions[i]}%`, width: `${Math.max(positions[i + 1] - positions[i], 0)}%`, top: 10, height: 2, backgroundColor: statusColor(n.statut) }} />
            ))}
            {sorted.map((n, i) => {
                const latest = i === sorted.length - 1
                const c = statusColor(n.statut)
                return (
                    <Box key={n.id ?? i} sx={{ position: 'absolute', top: 0, left: `${positions[i]}%`, transform: 'translateX(-50%)', width: 130, textAlign: 'center' }}>
                        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{`${getDate(n.date)} — ${statusLabel(n.statut)} · ${n.avancement}%${n.commentaire ? `\n${n.commentaire}` : ''}`}</span>}>
                            <Box
                                onClick={() => onEditNote(n)}
                                sx={{
                                    cursor: 'pointer', mx: 'auto',
                                    width: latest ? 18 : 12, height: latest ? 18 : 12, mt: latest ? '1px' : '4px',
                                    borderRadius: '50%',
                                    backgroundColor: latest ? c : '#fff',
                                    border: `3px solid ${c}`,
                                }}
                            />
                        </Tooltip>
                        <Box sx={{ fontSize: 11, fontWeight: 700, color: '#1c2340', mt: 0.5 }}>{getDate(n.date)}</Box>
                        <Box sx={{ fontSize: 11, fontWeight: latest ? 600 : 400, color: latest ? readableInk(statusColor(n.statut)) : '#6b7893' }}>{`${statusLabel(n.statut)} · ${n.avancement}%`}</Box>
                    </Box>
                )
            })}
            {showEcheance && (
                <Box sx={{ position: 'absolute', top: 0, left: `${echeancePos}%`, transform: 'translateX(-50%)', width: 120, textAlign: 'center' }}>
                    <Box sx={{ mx: 'auto', width: 12, height: 12, mt: '4px', borderRadius: '50%', backgroundColor: '#f5f7fa', border: `2px dashed ${echColor}` }} />
                    <Box sx={{ fontSize: 11, fontWeight: 700, color: '#6b7893', mt: 0.5 }}><DueDate lot={{ echeance, statut }} /></Box>
                    <Box sx={{ fontSize: 11, color: echColor }}>{echeanceOverdue ? 'Échéance dépassée' : 'Échéance'}</Box>
                </Box>
            )}
        </Box>
    )
}
Frise.propTypes = { notes: PropTypes.arrayOf(PropTypes.shape({})), echeance: PropTypes.number, statut: PropTypes.number, dueColor: PropTypes.string, onEditNote: PropTypes.func }

// -------------------------------------------------------------------------------------------------
// Carte d'un lot
// -------------------------------------------------------------------------------------------------
const LotCard = React.memo(({ lot, ideas = false, onEdit, onAddNote, onEditNote, onDelete }) => {
    const [notesExpanded, setNotesExpanded] = useState(false)
    const notes = lot.notes ?? []
    // Notes de la plus récente à la plus ancienne (accordéon : la 1re est la « dernière note »).
    const sortedNotes = [...notes].sort((a, b) => b.date - a.date)
    const lastNote = sortedNotes[0]
    const hasSeveralNotes = notes.length > 1
    const avct = lot.avancement ?? 0
    const color = statusColor(lot.statut)

    return (
        <Card sx={{ borderLeft: `4px solid ${color}`, '&:hover': { boxShadow: '0 1px 4px rgba(28,35,64,0.12)' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: '6px 16px', borderBottom: '1px solid #eef1f5', '& .MuiTypography-root': { lineHeight: 1.3 } }}>
                <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => onEdit(lot)}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1c2340' }}>{lot.nom}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#6b7893' }}>
                        {compact([lot.clientName, lot.chefName, lot.commentaire && truncateString(lot.commentaire, 200), ticketsLabel(lot)]).join(' · ')}
                    </Typography>
                </Box>
                {(lot.devis || lot.bonCommande) && (
                    <Box>
                        {lot.devis && <Typography sx={{ fontSize: 12, color: '#6b7893', whiteSpace: 'nowrap' }}>{`Devis ${lot.devis}`}</Typography>}
                        {lot.bonCommande && <Typography sx={{ fontSize: 12, color: '#6b7893', whiteSpace: 'nowrap' }}>{`BC ${lot.bonCommande}`}</Typography>}
                    </Box>
                )}
                {!ideas && !lot.bonCommande && <MissingBcIcon />}
                {!isNil(lot.total) && (
                    <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#1c2340', textAlign: 'right' }}>{`${formatMilliers(lot.total)} € HT`}</Typography>
                )}
                <StatusChip statut={lot.statut} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 132 }}>
                    <Box sx={{ flex: 1 }}><LinearProgress variant='determinate' value={avct} sx={progressBarSx(avct)} /></Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, width: 38, textAlign: 'right' }}>{`${avct}%`}</Typography>
                </Box>
                <Stack direction='row'>
                    <Tooltip title='Ajouter une note'><IconButton size='small' onClick={() => onAddNote(lot)}><Icon fontSize='small'>add_comment</Icon></IconButton></Tooltip>
                    <Tooltip title='Modifier le lot'><IconButton size='small' onClick={() => onEdit(lot)}><Icon fontSize='small'>edit</Icon></IconButton></Tooltip>
                    <Tooltip title='Supprimer le lot'><IconButton size='small' onClick={() => onDelete(lot)}><Icon fontSize='small' sx={{ color: 'red' }}>delete</Icon></IconButton></Tooltip>
                </Stack>
            </Box>
            <Box sx={{ p: '10px 16px 8px' }}>
                <Frise notes={notes} echeance={lot.echeance} statut={lot.statut} dueColor={dueDateColor(lot)} onEditNote={(n) => onEditNote(lot, n)} />
                {lastNote && (
                    <Box sx={{ mt: 0.5, borderRadius: '3px', backgroundColor: `${color}14`, border: `1px solid ${color}44`, overflow: 'hidden' }}>
                        <Box
                            onClick={hasSeveralNotes ? () => setNotesExpanded(v => !v) : undefined}
                            sx={{ p: '5px 12px', display: 'flex', gap: 1.25, alignItems: 'flex-start', cursor: hasSeveralNotes ? 'pointer' : 'default' }}
                        >
                            <Box sx={{ whiteSpace: 'nowrap' }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: readableInk(color) }}>
                                    {notesExpanded ? `Notes (${notes.length})` : 'Dernière note'}
                                </Typography>
                                {!notesExpanded && <Typography sx={{ fontSize: 11, color: '#6b7893' }}>{`Le ${getDate(lastNote.date)}`}</Typography>}
                            </Box>
                            {!notesExpanded && (
                                <Typography sx={{ fontSize: 13, color: '#33405c', whiteSpace: 'pre-line', flex: 1 }}>{lastNote.commentaire || `${statusLabel(lastNote.statut)} · ${lastNote.avancement}%`}</Typography>
                            )}
                            {hasSeveralNotes && <Icon fontSize='small' sx={{ color: '#6b7893', ml: 'auto' }}>{notesExpanded ? 'expand_less' : 'expand_more'}</Icon>}
                        </Box>
                        {notesExpanded && (
                            <Box>
                                {sortedNotes.map((note, i) => (
                                    <Box
                                        key={note.id ?? i}
                                        onClick={() => onEditNote(lot, note)}
                                        sx={{ p: '5px 12px', display: 'flex', gap: 1.25, borderTop: `1px solid ${color}44`, cursor: 'pointer', '&:hover': { backgroundColor: `${color}22` } }}
                                    >
                                        <Box sx={{ whiteSpace: 'nowrap', minWidth: 96 }}>
                                            <Typography sx={{ fontSize: 11, color: '#6b7893' }}>{getDate(note.date)}</Typography>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: statusColor(note.statut) }}>{`${statusLabel(note.statut)} · ${note.avancement}%`}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 13, color: '#33405c', whiteSpace: 'pre-line', flex: 1 }}>{note.commentaire}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Card>
    )
})
LotCard.displayName = 'LotCard'
LotCard.propTypes = {

    lot: PropTypes.object,
    ideas: PropTypes.bool,
    onEdit: PropTypes.func, onAddNote: PropTypes.func, onEditNote: PropTypes.func, onDelete: PropTypes.func,
}

export default LotCard
